import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

/**
 * 假优惠弹窗的领取状态。
 * 是否展示由活动接口 + 弹窗队列决定；本 store 只记录「已领取」，
 * 供付费墙消费（价格翻滚 / 支付按钮后缀 / 轮播暂停）。
 */

interface IFakeDiscountState {
    /** 本次会话是否已领取假优惠（点击 / 倒计时 / 遮罩 / back 均算领取） */
    claimed: boolean;
    /** 收口动效待播放：领取时置位，归属付费墙播放后立即消费；后续到墙只展示领取后状态，不重播金币飞行 */
    claimAnimationPending: boolean;
    /**
     * 弹窗展示期间注册的承载墙章节（popupVisible 时由对应 PayWall 注册）。
     * 领取动效是「哪面墙领的就在哪面墙播」，注册值在领取瞬间被快照为 claimPendingChapterId。
     * '' = 当前无墙承载弹窗。
     */
    claimHostChapterId: string;
    /**
     * 弹窗展示期间由承载墙注册的命中商品 id（与 claimHostChapterId 同步注册）。
     * 领取瞬间快照为 claimedProductId，作为 order_from=fake_offer 的归因依据。
     */
    claimHostProductId: string;
    /**
     * 假优惠任务已就位（到墙拉取已返回）但弹窗未展示时，由付费墙预注册的命中商品 id：
     * 命中卡从此时起即展示假价格，不等弹窗弹出。'' = 无待展示假优惠。
     */
    pendingTargetProductId: string;
    /** 领取瞬间快照的命中商品 id；'' = 未领取或领取时无命中商品 */
    claimedProductId: string;
    /**
     * 领取瞬间快照的归属章节。
     * - 与消费墙 chapterId 一致 → 该墙播放金币飞行 / 价格翻滚；
     * - ''（领取瞬间无墙在场）或与消费墙不一致 → 只清 pending，不重播动效。
     */
    claimPendingChapterId: string;
    /** 当前挂载的付费墙章节（PayWall mount/unmount 维护），用于判断归属墙是否仍在场 */
    mountedChapterIds: string[];
    /** 金币飞行已到位；仅 after-coin-fly 配置下作为价格翻滚的启动条件 */
    coinFlyDone: boolean;
    /** 价格翻滚已完成；默认 true 即「翻滚完成态」（真实价格），仅归属墙播放时先置 false 再定时恢复 */
    priceRollDone: boolean;
}

interface IFakeDiscountActions {
    /** renderer 在弹窗关闭动画完成时调用：快照归属墙并触发收口动效信号 */
    markClaimed: () => void;
    /** 弹窗在某面付费墙上展示期间注册承载墙；弹窗不再展示（含被 dismiss）时由该墙注销 */
    setClaimHost: (chapterId: string) => void;
    /** 弹窗展示期间由承载墙注册命中商品 id；弹窗不再展示时注销 */
    setClaimHostProduct: (productId: string) => void;
    /** 假优惠任务已就位、弹窗未展示时预注册命中商品 id；任务被消费 / 墙卸载时清空（传 ''） */
    setPendingTargetProduct: (productId: string) => void;
    /** PayWall 挂载 / 卸载时登记在场墙章节 */
    trackWallMounted: (chapterId: string, mounted: boolean) => void;
    /**
     * 付费墙消费待播放动效信号。
     * - 归属墙在场时只有归属墙可消费：非归属墙跳过且不清 pending，等归属墙消费，
     *   避免多墙并存时非归属墙先消费导致动效丢失；
     * - 归属墙不在场（含领取瞬间无墙在场，归属为 ''）→ 由先到任一面墙清 pending，不重播。
     * @param chapterId 消费墙章节 id
     * @returns 该墙是否为领取归属墙（应播放收口动效）
     */
    consumeClaimAnimation: (chapterId: string) => boolean;
    /** 金币飞行完成时置位；after-coin-fly 配置下价格翻滚以它为启动条件 */
    setCoinFlyDone: (done: boolean) => void;
    /** 归属墙播放翻滚时置 false，翻滚计时结束置 true；轮播/网格交互以它为恢复条件 */
    setPriceRollDone: (done: boolean) => void;
    /** 登录态切换时重置 */
    reset: () => void;
}

const initialState: IFakeDiscountState = {
    claimed: false,
    claimAnimationPending: false,
    claimHostChapterId: '',
    claimHostProductId: '',
    pendingTargetProductId: '',
    claimedProductId: '',
    claimPendingChapterId: '',
    mountedChapterIds: [],
    coinFlyDone: false,
    priceRollDone: true,
};

export const useFakeDiscountStore = create<IFakeDiscountState & IFakeDiscountActions>()(
    immer((set, get) => ({
        ...initialState,

        markClaimed: () => {
            set(state => {
                state.claimed = true;
                state.claimAnimationPending = true;
                // 领取动效绑定到弹窗打开时注册的承载墙
                state.claimPendingChapterId = state.claimHostChapterId;
                // 埋点归因绑定到弹窗展示期间注册的命中商品
                state.claimedProductId = state.claimHostProductId;
            });
        },

        setClaimHost: (chapterId: string) => {
            set(state => {
                state.claimHostChapterId = chapterId;
            });
        },

        setClaimHostProduct: (productId: string) => {
            set(state => {
                state.claimHostProductId = productId;
            });
        },

        setPendingTargetProduct: (productId: string) => {
            set(state => {
                state.pendingTargetProductId = productId;
            });
        },

        trackWallMounted: (chapterId: string, mounted: boolean) => {
            set(state => {
                if (mounted) {
                    // StrictMode 双挂载会重复登记，去重保持卸载时一一对应
                    if (!state.mountedChapterIds.includes(chapterId)) {
                        state.mountedChapterIds.push(chapterId);
                    }
                    return;
                }
                const index = state.mountedChapterIds.indexOf(chapterId);
                if (index >= 0) {
                    state.mountedChapterIds.splice(index, 1);
                }
                // 归属墙在翻滚未完成时卸载：翻滚计时随墙清理不会再置位，
                // 直接进完成态，避免后续到墙因 priceRollDone=false 多禁交互一个翻滚时长
                if (chapterId === state.claimPendingChapterId && state.claimed && !state.priceRollDone) {
                    state.priceRollDone = true;
                }
            });
        },

        consumeClaimAnimation: (chapterId: string) => {
            const { claimAnimationPending, claimPendingChapterId, mountedChapterIds } = get();
            if (!claimAnimationPending) {
                return false;
            }
            // 归属墙在场但不是本墙：留给归属墙消费，本墙不清 pending
            if (
                claimPendingChapterId !== '' &&
                claimPendingChapterId !== chapterId &&
                mountedChapterIds.includes(claimPendingChapterId)
            ) {
                return false;
            }
            set(state => {
                state.claimAnimationPending = false;
            });
            // 无归属（''）或归属不是本墙（归属墙已不在场）→ 只清信号不播动效
            return chapterId !== '' && claimPendingChapterId === chapterId;
        },

        setCoinFlyDone: (done: boolean) => {
            set(state => {
                state.coinFlyDone = done;
            });
        },

        setPriceRollDone: (done: boolean) => {
            set(state => {
                state.priceRollDone = done;
            });
        },

        reset: () => {
            set(() => ({ ...initialState }));
        },
    }))
);
